/**
 * The left rail's four views — Business, Customers, Channels, Operations (build handover §6).
 * Fixed wording/order/units. The rail only re-renders on save/select (`viewSel`); the centre
 * stays live off the draft the whole time. Each card opens a detail modal (`four-view-modal.tsx`).
 *
 * Visual simplification, noted for the record: the prototype's world dot-map (Customers) and
 * plant→site→route sankey (Channels) are rendered here as compact lists/tables instead of the
 * full SVG diagrams — the NUMBERS and WORDING they carry are unchanged and still real where the
 * plan says real (`atRiskValue`, `affectedAccounts`, `channelShare`, monthly sales), only the
 * illustration is lighter-weight. Business (sparkline bars) and Operations (the waffle grid, in
 * the same visual language as Pick Faces) are ported closer to the original.
 */

import type { ReactNode } from "react";

import {
  affectedAccounts,
  atRiskValue,
  channelShare,
  monthlyTradeSales,
  MONTHS,
  runScenario,
  type ScenarioContext,
  type ScenarioResult,
  type Site,
} from "@/lib/data";

import { ACCOUNTS, PLANTS, ROUTES, SECOND_SITE } from "./mock";
import type { SavedScenario, ViewModal } from "./state";

const fmt = (n: number) => Math.round(n).toLocaleString("en-US");

/* Mock: no feed carries this site's cost-to-serve or its share of network spend. */
const COST_TO_SERVE_CENTS = 3.2;
const NETWORK_SPEND_SHARE_PCT = 7.7;
const CAPITAL_AVOIDED_M = 4.2;

export interface FourViewsProps {
  scenarios: SavedScenario[];
  viewSel: number;
  onSelect: (index: number) => void;
  ctx: ScenarioContext;
  /** The problem-state result (all levers neutral) — what "Current state" (no scenario
   *  selected) shows, per build handover §7. */
  restResult: ScenarioResult;
  onOpen: (modal: ViewModal) => void;
}

export function selectedResult(
  scenarios: SavedScenario[],
  viewSel: number,
  ctx: ScenarioContext,
): ScenarioResult | null {
  const scn = viewSel >= 0 ? scenarios[viewSel] : undefined;
  return scn ? runScenario(scn.params, ctx) : null;
}

export function FourViews({
  scenarios,
  viewSel,
  onSelect,
  ctx,
  restResult,
  onOpen,
}: FourViewsProps) {
  const scn = viewSel >= 0 ? scenarios[viewSel] : undefined;
  const sm = scn ? runScenario(scn.params, ctx) : null;
  const { site } = ctx;

  return (
    <div className="rail-left">
      <div className="scn-bar">
        <div className="scn-lb">
          <span>Select scenario</span>
        </div>
        <select
          className="scn-select"
          value={viewSel}
          onChange={(e) => onSelect(Number(e.currentTarget.value))}
        >
          <option value={-1}>Current state</option>
          {scenarios.map((s, i) => (
            <option key={s.name} value={i}>
              {s.name}
            </option>
          ))}
        </select>
        <button className="btn-scenarios" disabled>
          View scenario reports
        </button>
      </div>
      <div className="views-list">
        <BusinessCard sm={sm} site={site} onOpen={onOpen} skus={ctx.skus} />
        <CustomersCard sm={sm} site={site} skus={ctx.skus} onOpen={onOpen} />
        <ChannelsCard sm={sm} skus={ctx.skus} onOpen={onOpen} />
        <OperationsCard
          sm={sm}
          rest={restResult}
          pinchYear={ctx.model.years?.[1] ?? 2027}
          onOpen={onOpen}
        />
      </div>
    </div>
  );
}

function Card({
  cls,
  label,
  question,
  base,
  n,
  d,
  sub,
  pill,
  rows,
  viz,
  onClick,
}: {
  cls: string;
  label: string;
  question: string;
  base?: string;
  n: string;
  d: string;
  sub?: string;
  pill?: string;
  rows: [string, string, string?][];
  viz: ReactNode;
  onClick: () => void;
}) {
  return (
    <div className={`fv ${cls}`} onClick={onClick} role="button" tabIndex={0}>
      <span className="fv-expand">Expand ⤢</span>
      <div className="fv-ch">
        <span className="fv-lb">{label}</span>
        <span className="fv-q">{question}</span>
      </div>
      {base ? <div className="fv-base">was {base}</div> : null}
      <div className="fv-big">
        <div className="fv-n">{n}</div>
        <div className="fv-bw">
          <div className="fv-d">{d}</div>
          {sub ? <div className="fv-sub">{sub}</div> : null}
          {pill ? <span className="fv-pill">{pill}</span> : null}
        </div>
      </div>
      {viz}
      <div className="fv-rows">
        {rows.map((r, i) => (
          <div className="fv-r" key={i}>
            <span className="fv-k">{r[0]}</span>
            <span className={`fv-v${r[2] ? ` ${r[2]}` : ""}`}>{r[1]}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function BusinessCard({
  sm,
  site,
  skus,
  onOpen,
}: {
  sm: ScenarioResult | null;
  site: Site;
  skus: ScenarioContext["skus"];
  onOpen: (m: ViewModal) => void;
}) {
  const ntsM = site.nts / 1_000_000;
  const atRisk = sm ? atRiskValue(sm.claim, skus) : 0;
  const { total, atRisk: riskByMonth } = monthlyTradeSales(skus, sm ? sm.claim : null);
  const maxTotal = Math.max(...total, 1);
  const capLine: [string, string, string?] =
    sm && sm.addFaces > 0
      ? ["Capital in", "in this case", "cost"]
      : sm
        ? ["Capital avoided", `£${CAPITAL_AVOIDED_M}m`, "get"]
        : ["Capital in", "none", ""];
  const bars = total.map((v, i) => {
    const h = (v / maxTotal) * 100;
    const riskH = riskByMonth[i] && v > 0 ? (riskByMonth[i] / v) * h : 0;
    return (
      <div className="fv-mcol" key={i}>
        <div className="fv-mbar">
          <i style={{ height: `${h.toFixed(1)}%` }}>
            {riskH > 0 ? <u style={{ height: `${Math.max(5, riskH).toFixed(1)}%` }} /> : null}
          </i>
        </div>
        <s>{MONTHS[i]}</s>
      </div>
    );
  });
  return (
    <Card
      cls={sm && atRisk > 0 ? "fv-cost" : "fv-rest"}
      label="Business"
      question="What does it cost you?"
      base={sm ? `£${ntsM.toFixed(0)}m trade sales carried` : undefined}
      n={sm ? `− £${atRisk.toFixed(1)}m` : `£${ntsM.toFixed(0)}m`}
      d={sm ? "trade sales at risk" : "trade sales carried"}
      rows={
        sm
          ? [capLine, ["Working capital", "scales with releases", "get"]]
          : [
              ["Cost to serve", `${COST_TO_SERVE_CENTS}c per unit`],
              ["Share of network spend", `${NETWORK_SPEND_SHARE_PCT}%`],
            ]
      }
      viz={
        <div className="fv-viz">
          <div className="fv-spark">{bars}</div>
        </div>
      }
      onClick={() => onOpen("business")}
    />
  );
}

function CustomersCard({
  sm,
  site,
  skus,
  onOpen,
}: {
  sm: ScenarioResult | null;
  site: Site;
  skus: ScenarioContext["skus"];
  onOpen: (m: ViewModal) => void;
}) {
  const namedAffected = sm ? ACCOUNTS.filter((a) => a.slow) : [];
  const affected = sm ? affectedAccounts(sm.claim, skus, site) : 0;
  const diamondPlatinum = ACCOUNTS.filter((a) => a.tier !== "Gold").length;
  const waiverAccounts = ACCOUNTS.filter((a) => a.waiver).length;
  return (
    <Card
      cls={sm && sm.waiverCount > 0 ? "fv-stop" : "fv-rest"}
      label="Customers"
      question="Who feels it?"
      base={sm ? `${fmt(site.accounts)} accounts served` : undefined}
      n={sm ? String(sm.waiverCount) : fmt(site.accounts)}
      d={sm ? "need sign-off" : "accounts served"}
      pill={sm && sm.waiverCount > 0 ? "blocker, not a price" : undefined}
      sub={sm ? `${fmt(affected)} accounts affected (upper bound, real Sku.accounts)` : undefined}
      rows={
        sm
          ? [
              ["Affected", `${fmt(affected)} of ${fmt(site.accounts)}`],
              ["Notice period", "60 days"],
            ]
          : [
              ["Diamond, Platinum", String(diamondPlatinum)],
              ["On contracted range", String(waiverAccounts)],
            ]
      }
      viz={
        <div className="fv-viz">
          <div className="fv-country-chips">
            {(namedAffected.length ? namedAffected : ACCOUNTS).slice(0, 6).map((a) => (
              <span key={a.name} className={sm && a.slow ? "chip lit" : "chip"}>
                {a.country}
              </span>
            ))}
          </div>
        </div>
      }
      onClick={() => onOpen("customers")}
    />
  );
}

function ChannelsCard({
  sm,
  skus,
  onOpen,
}: {
  sm: ScenarioResult | null;
  skus: ScenarioContext["skus"];
  onOpen: (m: ViewModal) => void;
}) {
  // Real: the share of the portfolio's NTS value sitting in SKUs relocate claimed. The PLDC
  // baseline/target and the route table it's interpolated against are wholly mock — see mock.ts.
  const movedPct = sm ? channelShare(sm.claim, skus) : 0;
  const pldcLoad =
    SECOND_SITE.loadBefore + (SECOND_SITE.loadAfter - SECOND_SITE.loadBefore) * (movedPct / 100);
  const largest = ROUTES.reduce((a, r) => (r.share > a.share ? r : a), ROUTES[0]!);
  const smallest = ROUTES.reduce((a, r) => (r.share < a.share ? r : a), ROUTES[0]!);
  return (
    <Card
      cls={sm && movedPct > 0 ? "fv-cost" : "fv-rest"}
      label="Channels"
      question="What changes route?"
      base={sm ? `${ROUTES.length} routes in use` : undefined}
      n={sm ? `${movedPct.toFixed(1)}%` : String(ROUTES.length)}
      d={sm ? "of volume changes route" : "routes in use"}
      sub={sm && movedPct > 0 ? "mock second-site story, scaled by real relocation" : undefined}
      rows={
        sm
          ? [
              ["Crosses to " + SECOND_SITE.name, `${movedPct.toFixed(1)}%`, "cost"],
              [SECOND_SITE.name + " load", `${SECOND_SITE.loadBefore}% → ${pldcLoad.toFixed(1)}%`],
            ]
          : [
              ["Largest route", `${largest?.name}, ${largest?.share}%`],
              ["Smallest route", `${smallest?.name}, ${smallest?.share}%`],
            ]
      }
      viz={
        <div className="fv-viz">
          <div className="fv-plant-row">
            {PLANTS.map((p) => (
              <span key={p.name} className="chip">
                {p.name} {p.share}%
              </span>
            ))}
          </div>
        </div>
      }
      onClick={() => onOpen("channels")}
    />
  );
}

function OperationsCard({
  sm,
  rest,
  pinchYear,
  onOpen,
}: {
  sm: ScenarioResult | null;
  rest: ScenarioResult;
  pinchYear: number;
  onOpen: (m: ViewModal) => void;
}) {
  const active = sm ?? rest;
  const room = active.room;
  const heldPinch = active.held[pinchYear] ?? 0;
  const used = active.ceiling > 0 ? heldPinch / active.ceiling : 0;
  const cells = Array.from({ length: 40 }, (_, i) => {
    const frac = i / 40;
    return frac < Math.min(1, used) ? "used" : room >= 0 ? "room" : "short";
  });
  return (
    <Card
      cls={room >= 0 ? "fv-get" : "fv-short"}
      label="Operations"
      question="What do you get back?"
      base={sm ? `${fmt(-rest.room)} slots short by plan` : undefined}
      n={sm ? (room >= 0 ? `+ ${fmt(room)}` : fmt(-room)) : fmt(-rest.room)}
      d={sm ? (room >= 0 ? "slots of room created" : "slots still needed") : "slots short by plan"}
      rows={
        sm
          ? [
              [
                "Released by the levers",
                sm.released > 0 ? `+ ${fmt(sm.released)}` : "none",
                sm.released > 0 ? "get" : undefined,
              ],
              [
                "Added as capacity",
                sm.addFaces > 0 ? `+ ${fmt(sm.addFaces)}` : "none",
                sm.addFaces > 0 ? "get" : undefined,
              ],
            ]
          : [
              ["Held as planned", fmt(rest.held[pinchYear] ?? 0)],
              ["Pick-face ceiling", fmt(rest.ceiling)],
            ]
      }
      viz={
        <div className="fv-viz">
          <div className="fv-waffle">
            {cells.map((c, i) => (
              <i key={i} className={c} />
            ))}
          </div>
        </div>
      }
      onClick={() => onOpen("operations")}
    />
  );
}
